/**
 * BeamerPitchView — v2
 *
 * Volledig scherm beamerpresentatie voor fysieke bijeenkomsten.
 *
 * Nieuw in v2:
 *  - Zelfde overlay-elementen als VideoMeetingOverlay (logo fade, timer, banner, NEXT)
 *  - Automatische foto-timing: pitchduur / aantal foto's (bijv. 60s / 5 = 12s per foto)
 *  - PDF/afbeelding als template-achtergrond (instelbaar door organisator)
 *  - Video als volledig-scherm achtergrond
 *  - OverlaySettings doorgegeven voor consistente look
 *
 * Props uitgebreid met overlaySettings, clubLogoUrl, templateBackgroundUrl.
 * Backwards compatible — nieuwe props zijn optioneel.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { MediaItem } from '../hooks/usePitchMedia';
import type { TemplateZone } from '../networkClubTypes';
import PitchTransitionSlide from './PitchTransitionSlide';
import type { OverlaySettings } from './VideoMeetingOverlay';
import { loadPdfDocumentFromUrl } from '../loadPdfDocumentFromUrl';

interface TimelineSlot {
  key: string;
  label: string;
  slotNumber: number;
  slotType: string;
  userId?: string;
  isAbsent: boolean;
  isActive: boolean;
  isDone: boolean;
}

interface LivePitchEntry {
  userId: string;
  presenterName: string;
  title: string;
  message: string;
  searchQuestion?: string;
  recommendation?: { targetName: string; reason: string };
  mediaItems?: MediaItem[];
  mediaDisplaySeconds?: number;
  photoURL?: string;
  enabled?: boolean;
}

interface BeamerPitchViewProps {
  runningEntry: LivePitchEntry | null;
  remainingSeconds: number;
  totalSeconds?: number;          // nieuw: voor voortgangsbalk
  timelineSlots: TimelineSlot[];
  isHost: boolean;
  primaryColor?: string;
  accentColor?: string;
  // Overlay-instellingen (zelfde als VideoMeetingOverlay)
  overlaySettings?: Partial<OverlaySettings>;
  clubLogoUrl?: string;
  // Template-achtergrond (PDF als afbeelding, Canva export)
  templateBackgroundUrl?: string;
  /** Eerste pagina van geüploade PDF (meetingSettings.templatePdfUrl) */
  templatePdfUrl?: string;
  /** Actieve PDF-pagina (vanuit presentatie-tijdlijn), standaard 1 */
  activePageNumber?: number;
  /** 1-pagina pitch-achtergrond PDF (per lid ingevuld) */
  pitchTemplatePdfUrl?: string;
  /** Geconfigureerde zones voor dynamische velden op de pitch-template */
  pitchTemplateZones?: TemplateZone[];
  // Volgende spreker
  nextEntry?: LivePitchEntry | null;
  nextSlotNumber?: number;
  // Overgang-instellingen
  transitionActive: boolean;
  transitionNextEntry: LivePitchEntry | null;
  transitionNextSlotNumber: number;
  totalPitchSlots: number;
  transitionDurationSeconds?: number;
  transitionMusicUrl?: string | null;
  transitionMusicVolume?: number;
  onTransitionComplete: () => void;
  onStopPitch: () => void;
}

// ── Standaard overlay-instellingen ────────────────────────────────────────────
const OV_DEFAULTS: OverlaySettings = {
  bannerColor:           '#1D4ED8',
  bannerTextColor:       '#FFFFFF',
  timerColor:            '#FFFFFF',
  numberColor:           '#FFFFFF',
  logoMode:              'alternating',
  logoSwitchIntervalSec: 10,
  logoFadeDurationSec:   1.5,
  bannerContent:         'searchQuestion',
  numberPrefix:          'NR:',
};
function mergeOv(s: Partial<OverlaySettings> | undefined): OverlaySettings {
  return { ...OV_DEFAULTS, ...(s || {}) };
}

function formatTimer(secs: number): string {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s} SEC`;
}

/** PDF-pagina als cover-achtergrond (standaard pagina 1, instelbaar via pageNumber). */
const BeamerPdfCover: React.FC<{ url: string; pageNumber?: number }> = ({ url, pageNumber = 1 }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const destroyedRef = useRef(false);
  const paintGenRef = useRef(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pdfRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const activeRenderTaskRef = useRef<{ cancel: (extraDelay?: number) => void } | null>(null);

  useEffect(() => {
    destroyedRef.current = false;
    let ro: ResizeObserver | null = null;

    const paint = async () => {
      const pdf = pdfRef.current;
      const wrap = wrapRef.current;
      const canvas = canvasRef.current;
      if (destroyedRef.current || !pdf || !wrap || !canvas) return;
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (w < 4 || h < 4) return;
      const gen = ++paintGenRef.current;
      activeRenderTaskRef.current?.cancel();
      activeRenderTaskRef.current = null;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let task: any = null;
      try {
        const safePageNum = Math.max(1, Math.min(pageNumber, pdf.numPages));
        const page = await pdf.getPage(safePageNum);
        if (destroyedRef.current || paintGenRef.current !== gen) return;
        const base = page.getViewport({ scale: 1 });
        const scale = Math.min(w / base.width, h / base.height);
        const viewport = page.getViewport({ scale });
        const cw = Math.floor(viewport.width);
        const ch = Math.floor(viewport.height);
        canvas.width = cw;
        canvas.height = ch;
        canvas.style.position = 'absolute';
        canvas.style.left = `${Math.floor((w - cw) / 2)}px`;
        canvas.style.top = `${Math.floor((h - ch) / 2)}px`;
        task = page.render({ canvas, viewport, background: '#0a0f1a' });
        activeRenderTaskRef.current = task;
        await task.promise;
      } catch {
        /* CORS, corrupt PDF, of geannuleerde render — achtergrond blijft donker */
      } finally {
        if (task && activeRenderTaskRef.current === task) {
          activeRenderTaskRef.current = null;
        }
      }
    };

    void (async () => {
      try {
        const pdf = await loadPdfDocumentFromUrl(url);
        if (destroyedRef.current) {
          pdf.destroy?.();
          return;
        }
        pdfRef.current = pdf;
        await paint();
        ro = new ResizeObserver(() => void paint());
        if (wrapRef.current) ro.observe(wrapRef.current);
      } catch {
        /* ignore */
      }
    })();

    return () => {
      destroyedRef.current = true;
      activeRenderTaskRef.current?.cancel();
      activeRenderTaskRef.current = null;
      ro?.disconnect();
      pdfRef.current?.destroy?.();
      pdfRef.current = null;
    };
  }, [url, pageNumber]);

  return (
    <div ref={wrapRef} style={{ position: 'absolute', inset: 0, overflow: 'hidden', background: '#0a0f1a' }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

// ── Achtergrond media-component ───────────────────────────────────────────────
// Foto's als volledig-scherm achtergrond met automatische timing
const BackgroundMedia: React.FC<{
  items: MediaItem[];
  pitchDurationSeconds: number;
  templatePdfUrl?: string;
  templateUrl?: string;
  activePageNumber?: number;
}> = ({ items, pitchDurationSeconds, templatePdfUrl, templateUrl, activePageNumber }) => {
  const images = items.filter((m) => m.type === 'image');
  const video  = items.find((m)  => m.type === 'video' || m.type === 'youtube');
  const [idx,  setIdx]  = useState(0);
  const [fade, setFade] = useState(true);

  // Auto-timing: pitchduur / aantal foto's
  const autoSecondsPerPhoto = images.length > 0
    ? Math.max(3, Math.floor(pitchDurationSeconds / images.length))
    : pitchDurationSeconds;

  useEffect(() => { setIdx(0); setFade(true); }, [items]);

  useEffect(() => {
    if (images.length <= 1) return;
    const sec = images[idx]?.displaySeconds ?? autoSecondsPerPhoto;
    const t = setTimeout(() => {
      setFade(false);
      setTimeout(() => {
        setIdx((p) => (p + 1) % images.length);
        setFade(true);
      }, 600);
    }, sec * 1000);
    return () => clearTimeout(t);
  }, [idx, images, autoSecondsPerPhoto]);

  if (video) {
    if (video.type === 'youtube' && video.youtubeVideoId) {
      return (
        <iframe
          src={`https://www.youtube.com/embed/${video.youtubeVideoId}?autoplay=1&mute=1&controls=0&loop=${video.loop ? 1 : 0}&playlist=${video.youtubeVideoId}`}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
          allow="autoplay; fullscreen"
          allowFullScreen
          title="Achtergrond video"
        />
      );
    }
    return (
      <video
        src={video.storageUrl}
        autoPlay muted loop={video.loop ?? true} playsInline
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }

  if (images.length > 0) {
    const photo = images[idx];
    return (
      <img
        key={photo.id}
        src={photo.storageUrl}
        alt=""
        style={{
          position: 'absolute', inset: 0, width: '100%', height: '100%',
          objectFit: 'cover', opacity: fade ? 1 : 0,
          transition: 'opacity 0.6s ease',
        }}
      />
    );
  }

  // PDF-template vóór statische afbeelding (meetingSettings)
  if (templatePdfUrl) {
    return <BeamerPdfCover url={templatePdfUrl} pageNumber={activePageNumber} />;
  }
  if (templateUrl) {
    return (
      <img
        src={templateUrl}
        alt="template"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
      />
    );
  }

  return null;
};

// ── Logo fade-component ───────────────────────────────────────────────────────
const FadingLogo: React.FC<{
  clubLogoUrl?: string;
  memberPhotoUrl?: string;
  cfg: OverlaySettings;
}> = ({ clubLogoUrl, memberPhotoUrl, cfg }) => {
  const [showClub, setShowClub] = useState(true);
  const [opacity,  setOpacity]  = useState(1);
  const ref = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (cfg.logoMode !== 'alternating') {
      setShowClub(cfg.logoMode === 'club');
      setOpacity(1);
      return;
    }
    const intervalMs = cfg.logoSwitchIntervalSec * 1000;
    const halfFade   = (cfg.logoFadeDurationSec * 1000) / 2;
    const cycle = () => {
      setOpacity(0);
      setTimeout(() => { setShowClub((p) => !p); setOpacity(1); }, halfFade);
    };
    ref.current = setInterval(cycle, intervalMs);
    return () => { if (ref.current) clearInterval(ref.current); };
  }, [cfg.logoMode, cfg.logoSwitchIntervalSec, cfg.logoFadeDurationSec]);

  const src    = showClub ? clubLogoUrl    : memberPhotoUrl;
  const init   = showClub ? 'C'           : '?';
  const fadeDur = `${cfg.logoFadeDurationSec}s`;

  return (
    <div style={{
      width: 56, height: 56, borderRadius: 12, overflow: 'hidden',
      background: cfg.bannerColor, display: 'flex', alignItems: 'center',
      justifyContent: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
      opacity, transition: `opacity ${fadeDur} ease`,
    }}>
      {src ? (
        <img src={src} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
      ) : (
        <span style={{ fontSize: 20, fontWeight: 900, color: cfg.bannerTextColor }}>{init}</span>
      )}
    </div>
  );
};

// ── Tijdlijn onderaan ─────────────────────────────────────────────────────────
const BeamerTimeline: React.FC<{
  slots: TimelineSlot[];
  accentColor: string;
}> = ({ slots, accentColor }) => (
  <div
    style={{
      width: '100%', overflowX: 'auto', padding: '10px 16px',
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
      borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 'max-content', margin: '0 auto', width: 'fit-content' }}>
      {slots.map((slot, i) => (
        <React.Fragment key={slot.key}>
          <div
            title={`${slot.label} (#${slot.slotNumber})`}
            style={{
              height: 28, padding: '0 10px', borderRadius: 8,
              fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', gap: 4,
              whiteSpace: 'nowrap', opacity: slot.isAbsent ? 0.3 : 1,
              ...(slot.isActive
                ? { background: accentColor, color: '#fff', boxShadow: `0 0 10px ${accentColor}99` }
                : slot.isDone
                ? { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)' }
                : slot.slotType === 'pitch'
                ? { background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.75)' }
                : { background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)' }),
            }}
          >
            {slot.isDone && <span>✓</span>}
            {slot.isAbsent && <span>✗</span>}
            <span>{slot.label}</span>
          </div>
          {i < slots.length - 1 && (
            <div style={{ width: 14, height: 1, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// ── Hoofd-component ───────────────────────────────────────────────────────────
const BeamerPitchView: React.FC<BeamerPitchViewProps> = ({
  runningEntry,
  remainingSeconds,
  totalSeconds,
  timelineSlots,
  isHost,
  primaryColor = '#0f172a',
  accentColor = '#f97316',
  overlaySettings,
  clubLogoUrl,
  templateBackgroundUrl,
  templatePdfUrl,
  activePageNumber = 1,
  pitchTemplatePdfUrl,
  pitchTemplateZones,
  nextEntry,
  nextSlotNumber,
  transitionActive,
  transitionNextEntry,
  transitionNextSlotNumber,
  totalPitchSlots,
  transitionDurationSeconds = 10,
  transitionMusicUrl,
  transitionMusicVolume = 0.5,
  onTransitionComplete,
  onStopPitch,
}) => {
  const cfg       = mergeOv(overlaySettings);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pitchDur   = totalSeconds ?? 60;

  const bannerText: string | null = (() => {
    if (!runningEntry) return null;
    switch (cfg.bannerContent) {
      case 'searchQuestion': return runningEntry.searchQuestion ? `ZOEK VRAAG: ${runningEntry.searchQuestion.toUpperCase()}` : null;
      case 'nameCompany':    return runningEntry.presenterName.toUpperCase();
      case 'message':        return runningEntry.message?.toUpperCase() || null;
      case 'title':          return runningEntry.title?.toUpperCase() || null;
      default:               return null;
    }
  })();

  const progressPct = pitchDur > 0
    ? Math.max(0, Math.min(100, (remainingSeconds / pitchDur) * 100))
    : 0;

  // Actieve slot-label voor de permanente timer rechtsonder
  const activeSlotLabel: string = runningEntry
    ? (runningEntry.title || runningEntry.presenterName || '').toUpperCase()
    : (timelineSlots.find(s => s.isActive)?.label ?? '').toUpperCase();

  const handleStopDebounced = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => onStopPitch(), 300);
  }, [onStopPitch]);

  useEffect(() => () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); }, []);

  // Preload afbeeldingen
  useEffect(() => {
    runningEntry?.mediaItems?.filter((m) => m.type === 'image').forEach((m) => {
      const img = new Image(); img.src = m.storageUrl;
    });
  }, [runningEntry?.userId]);

  const slotNumber = timelineSlots.find((s) => s.userId === runningEntry?.userId)?.slotNumber ?? 0;
  const hasIdleTemplate = !!(templatePdfUrl || templateBackgroundUrl);
  const hasPitchTemplate = !!(pitchTemplatePdfUrl && pitchTemplateZones && pitchTemplateZones.length > 0);
  const zoneMap = new Map((pitchTemplateZones ?? []).map((z) => [z.field, z]));

  return (
    <div
      style={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#0a0f1a',
        overflow: 'hidden',
      }}
    >

      {/* ── Overgangsslide ───────────────────────────────────────────────── */}
      {transitionActive && transitionNextEntry && (
        <PitchTransitionSlide
          nextPresenterName={transitionNextEntry.presenterName}
          nextSlotNumber={transitionNextSlotNumber}
          totalSlots={totalPitchSlots}
          durationSeconds={transitionDurationSeconds}
          musicUrl={transitionMusicUrl}
          musicVolume={transitionMusicVolume}
          primaryColor={primaryColor}
          accentColor={accentColor}
          isHost={isHost}
          onComplete={onTransitionComplete}
          onSkip={onTransitionComplete}
        />
      )}

      {!transitionActive && (
        <>
          {/* ── Achtergrond-laag ─────────────────────────────────────────── */}
          {hasPitchTemplate && runningEntry ? (
            /* Pitch-template achtergrond (1-page PDF per lid) */
            <BeamerPdfCover url={pitchTemplatePdfUrl!} />
          ) : (runningEntry || hasIdleTemplate) ? (
            <BackgroundMedia
              items={runningEntry?.mediaItems ?? []}
              pitchDurationSeconds={pitchDur}
              templatePdfUrl={templatePdfUrl}
              templateUrl={templateBackgroundUrl}
              activePageNumber={activePageNumber}
            />
          ) : null}

          {/* Donker overlay — niet bij pitch-template (het ontwerp IS de achtergrond) */}
          {!(hasPitchTemplate && runningEntry) && (
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.55) 100%)' }} />
          )}

          {/* ── Pitch-inhoud: zone-gebaseerd of gecentreerd fallback ───── */}
          {hasPitchTemplate && runningEntry ? (
            /* Zone-based rendering: elke zone positioneert een veld op de template */
            <div style={{ position: 'absolute', inset: 0 }}>
              {/* Photo zone */}
              {zoneMap.has('photo') && runningEntry.photoURL && (() => {
                const z = zoneMap.get('photo')!;
                return (
                  <div style={{
                    position: 'absolute',
                    left: `${z.x}%`, top: `${z.y}%`,
                    width: `${z.w}%`, height: `${z.h}%`,
                    overflow: 'hidden', borderRadius: '50%',
                  }}>
                    <img
                      src={runningEntry.photoURL}
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                );
              })()}

              {/* Name zone */}
              {zoneMap.has('name') && (() => {
                const z = zoneMap.get('name')!;
                const fontSize = z.fontSize || 64;
                return (
                  <div style={{
                    position: 'absolute',
                    left: `${z.x}%`, top: `${z.y}%`,
                    width: `${z.w}%`, height: `${z.h}%`,
                    display: 'flex', alignItems: z.align === 'center' ? 'center' : 'flex-end',
                    justifyContent: z.align === 'right' ? 'flex-end' : z.align === 'center' ? 'center' : 'flex-start',
                    textAlign: z.align || 'left',
                    overflow: 'hidden',
                  }}>
                    <span style={{
                      fontSize: `clamp(${fontSize * 0.4}px, ${fontSize / 19.2}vw, ${fontSize}px)`,
                      fontWeight: 900, color: z.color || '#fff',
                      textShadow: '0 2px 12px rgba(0,0,0,0.5)',
                      lineHeight: 1.15, wordBreak: 'break-word',
                    }}>
                      {runningEntry.presenterName}
                    </span>
                  </div>
                );
              })()}

              {/* Title zone */}
              {zoneMap.has('title') && runningEntry.title && (() => {
                const z = zoneMap.get('title')!;
                const fontSize = z.fontSize || 32;
                return (
                  <div style={{
                    position: 'absolute',
                    left: `${z.x}%`, top: `${z.y}%`,
                    width: `${z.w}%`, height: `${z.h}%`,
                    display: 'flex',
                    justifyContent: z.align === 'right' ? 'flex-end' : z.align === 'center' ? 'center' : 'flex-start',
                    alignItems: 'center',
                    overflow: 'hidden',
                  }}>
                    <span style={{
                      fontSize: `clamp(${fontSize * 0.4}px, ${fontSize / 19.2}vw, ${fontSize}px)`,
                      fontWeight: 700, color: z.color || '#fff',
                      textShadow: '0 1px 8px rgba(0,0,0,0.4)',
                      lineHeight: 1.2,
                    }}>
                      {runningEntry.title}
                    </span>
                  </div>
                );
              })()}

              {/* Search question zone */}
              {zoneMap.has('searchQuestion') && runningEntry.searchQuestion && (() => {
                const z = zoneMap.get('searchQuestion')!;
                const fontSize = z.fontSize || 20;
                return (
                  <div style={{
                    position: 'absolute',
                    left: `${z.x}%`, top: `${z.y}%`,
                    width: `${z.w}%`, height: `${z.h}%`,
                    display: 'flex', alignItems: 'center',
                    justifyContent: z.align === 'right' ? 'flex-end' : z.align === 'center' ? 'center' : 'flex-start',
                    overflow: 'hidden',
                  }}>
                    <span style={{
                      fontSize: `clamp(${fontSize * 0.4}px, ${fontSize / 19.2}vw, ${fontSize}px)`,
                      fontWeight: 600, color: z.color || '#fde68a',
                      textShadow: '0 1px 6px rgba(0,0,0,0.4)',
                      lineHeight: 1.3,
                    }}>
                      {runningEntry.searchQuestion}
                    </span>
                  </div>
                );
              })()}

              {/* Message zone */}
              {zoneMap.has('message') && runningEntry.message && (() => {
                const z = zoneMap.get('message')!;
                const fontSize = z.fontSize || 20;
                return (
                  <div style={{
                    position: 'absolute',
                    left: `${z.x}%`, top: `${z.y}%`,
                    width: `${z.w}%`, height: `${z.h}%`,
                    display: 'flex', alignItems: 'center',
                    justifyContent: z.align === 'right' ? 'flex-end' : z.align === 'center' ? 'center' : 'flex-start',
                    overflow: 'hidden',
                  }}>
                    <span style={{
                      fontSize: `clamp(${fontSize * 0.4}px, ${fontSize / 19.2}vw, ${fontSize}px)`,
                      fontWeight: 500, color: z.color || '#fff',
                      textShadow: '0 1px 6px rgba(0,0,0,0.3)',
                      lineHeight: 1.4,
                    }}>
                      {runningEntry.message}
                    </span>
                  </div>
                );
              })()}

              {/* Recommendation zone */}
              {zoneMap.has('recommendation') && runningEntry.recommendation?.targetName && (() => {
                const z = zoneMap.get('recommendation')!;
                const fontSize = z.fontSize || 18;
                return (
                  <div style={{
                    position: 'absolute',
                    left: `${z.x}%`, top: `${z.y}%`,
                    width: `${z.w}%`, height: `${z.h}%`,
                    display: 'flex', alignItems: 'center',
                    justifyContent: z.align === 'right' ? 'flex-end' : z.align === 'center' ? 'center' : 'flex-start',
                    overflow: 'hidden',
                  }}>
                    <span style={{
                      fontSize: `clamp(${fontSize * 0.4}px, ${fontSize / 19.2}vw, ${fontSize}px)`,
                      fontWeight: 600, color: z.color || '#fde68a',
                      textShadow: '0 1px 6px rgba(0,0,0,0.4)',
                      lineHeight: 1.3,
                    }}>
                      ★ {runningEntry.recommendation.targetName} — {runningEntry.recommendation.reason}
                    </span>
                  </div>
                );
              })()}
            </div>
          ) : (
            /* Fallback: standaard gecentreerde pitch-layout */
            <div style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '40px 40px 20px', gap: 12 }}>
              {runningEntry ? (
                <>
                  <h2 style={{ fontSize: 'clamp(2.5rem,6vw,5rem)', fontWeight: 900, color: '#fff', margin: 0, textShadow: '0 2px 24px rgba(0,0,0,0.7)', lineHeight: 1.1 }}>
                    {runningEntry.presenterName}
                  </h2>
                  {runningEntry.title && (
                    <h3 style={{ fontSize: 'clamp(1.2rem,3vw,2.5rem)', fontWeight: 700, color: accentColor, margin: 0, textShadow: '0 1px 12px rgba(0,0,0,0.5)' }}>
                      {runningEntry.title}
                    </h3>
                  )}
                  {runningEntry.message && (
                    <p style={{ fontSize: 'clamp(0.9rem,2vw,1.4rem)', color: 'rgba(255,255,255,0.85)', maxWidth: 700, lineHeight: 1.5, margin: 0 }}>
                      {runningEntry.message}
                    </p>
                  )}
                  {runningEntry.recommendation?.targetName && (
                    <div style={{ padding: '8px 20px', borderRadius: 12, background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)', fontSize: 14, color: '#FDE68A', maxWidth: 560 }}>
                      ★ <strong>{runningEntry.recommendation.targetName}</strong> — {runningEntry.recommendation.reason}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}

          {/* ── OVERLAY ELEMENTEN (zelfde als VideoMeetingOverlay) ────────── */}
          {runningEntry && (
            <>
              {/* Logo + timer (linksboven) */}
              <div style={{ position: 'absolute', top: 16, left: 16, pointerEvents: 'none' }}>
                <FadingLogo
                  clubLogoUrl={clubLogoUrl}
                  memberPhotoUrl={runningEntry.photoURL}
                  cfg={cfg}
                />
                <div style={{ marginTop: 6, fontSize: 22, fontWeight: 900, color: remainingSeconds <= 10 ? '#EF4444' : cfg.timerColor, textShadow: '0 1px 8px rgba(0,0,0,0.6)', letterSpacing: 1, transition: 'color 0.3s ease' }}>
                  {formatTimer(remainingSeconds)}
                </div>
                {/* Voortgangsbalk */}
                <div style={{ width: 56, height: 4, background: 'rgba(255,255,255,0.2)', borderRadius: 4, marginTop: 4, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${progressPct}%`,
                    background: remainingSeconds <= 10 ? '#EF4444' : cfg.timerColor,
                    borderRadius: 4, transition: 'width 1s linear, background 0.3s ease',
                  }} />
                </div>
              </div>

              {/* NEXT — kaart rechtsboven met naam zichtbaar */}
              {nextEntry && (
                <div style={{ position: 'absolute', top: 16, right: 16, textAlign: 'right', pointerEvents: 'none' }}>
                  <div style={{ fontSize: 9, fontWeight: 800, color: 'rgba(255,255,255,0.55)', letterSpacing: 1.5, marginBottom: 5, textTransform: 'uppercase' }}>Volgende</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)', borderRadius: 12, padding: '8px 12px', border: '1px solid rgba(255,255,255,0.12)' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: '#fff', lineHeight: 1.2, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nextEntry.presenterName}</div>
                      {nextEntry.title && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 1, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nextEntry.title}</div>}
                    </div>
                    <div style={{ width: 36, height: 36, borderRadius: 8, overflow: 'hidden', flexShrink: 0, border: '1.5px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {nextEntry.photoURL
                        ? <img src={nextEntry.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <span style={{ fontSize: 14, fontWeight: 900, color: '#fff' }}>{nextEntry.presenterName[0]?.toUpperCase()}</span>
                      }
                    </div>
                  </div>
                </div>
              )}

              {/* Stop pitch knop (host, linksonder) */}
              {isHost && (
                <button
                  onClick={handleStopDebounced}
                  style={{ position: 'absolute', bottom: 56, left: 16, minHeight: 36, padding: '0 16px', borderRadius: 10, fontSize: 12, fontWeight: 600, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', backdropFilter: 'blur(4px)' }}
                >
                  Stop pitch
                </button>
              )}

              {/* Banner onderin */}
              {bannerText && (
                <div style={{ position: 'absolute', bottom: 44, left: 0, right: 0, background: cfg.bannerColor, padding: '10px 20px' }}>
                  <div style={{ fontSize: 'clamp(13px,2vw,18px)', fontWeight: 900, color: cfg.bannerTextColor, letterSpacing: 0.8, textTransform: 'uppercase', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {bannerText}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Permanente timer rechtsonder — Mastermind stijl ─────────────── */}
      {remainingSeconds > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 52,
          right: 24,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
          pointerEvents: 'none',
        }}>
          {/* Circulaire avatar — alleen bij actieve pitcher */}
          {runningEntry && (
            <div style={{ position: 'relative' }}>
              {runningEntry.photoURL ? (
                <img
                  src={runningEntry.photoURL}
                  alt=""
                  style={{
                    width: 'clamp(56px,7vw,80px)',
                    height: 'clamp(56px,7vw,80px)',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '3px solid rgba(255,255,255,0.85)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
                    display: 'block',
                  }}
                />
              ) : (
                <div style={{
                  width: 'clamp(56px,7vw,80px)',
                  height: 'clamp(56px,7vw,80px)',
                  borderRadius: '50%',
                  border: '3px solid rgba(255,255,255,0.85)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
                  background: 'rgba(255,255,255,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(20px,3vw,30px)',
                  fontWeight: 900,
                  color: '#fff',
                }}>
                  {runningEntry.presenterName[0]?.toUpperCase()}
                </div>
              )}
            </div>
          )}

          {/* Naam */}
          {activeSlotLabel && (
            <div style={{
              fontSize: 'clamp(10px,1.3vw,14px)',
              fontWeight: 900,
              color: '#fff',
              letterSpacing: 2,
              textTransform: 'uppercase',
              textShadow: '0 2px 8px rgba(0,0,0,0.8)',
              textAlign: 'center',
              maxWidth: 'clamp(120px,15vw,200px)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.2,
            }}>
              {activeSlotLabel}
            </div>
          )}

          {/* Grote timer */}
          <div style={{
            fontSize: 'clamp(2.4rem,5vw,4.5rem)',
            fontWeight: 900,
            color: remainingSeconds <= 10 ? '#EF4444' : '#fff',
            textShadow: '0 2px 16px rgba(0,0,0,0.8)',
            letterSpacing: 2,
            lineHeight: 1,
            transition: 'color 0.3s ease',
            fontVariantNumeric: 'tabular-nums',
          }}>
            {formatTimer(remainingSeconds)}
          </div>
        </div>
      )}

      {/* ── Tijdlijn onderaan ─────────────────────────────────────────────── */}
      <BeamerTimeline slots={timelineSlots} accentColor={accentColor} />
    </div>
  );
};

export default BeamerPitchView;
